(() => {
  'use strict';

  const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const shapeNames = { oval: 'Oval', d: 'D-Form', angular: 'Eckig' };

  function initialize(root) {
    if (root.dataset.bsCatReady === 'true') return;
    const get = (name) => root.querySelector(`[data-bs-cat-${name}]`);
    const brandInput = get('brand');
    const seriesInput = get('series');
    const seriesField = get('series-field');
    const shapeInput = get('shape');
    const count = get('count');
    const grid = get('grid');
    const message = get('message');
    const messageTitle = get('message-title');
    const messageCopy = get('message-copy');
    const messageLink = get('message-link');
    const filters = get('filters');
    const shapePanel = get('shape-panel');
    const help = get('help');
    const resultsHead = get('results-head');
    const footnote = get('footnote');
    const supportLinks = [...root.querySelectorAll('[data-bs-cat-support]')].map((node) => ({ node, base: node.getAttribute('href') }));
    const cards = [...root.querySelectorAll('[data-bs-cat-card]')].map((node) => {
      let data = {};
      try { data = JSON.parse(node.dataset.bsFit || '{}'); } catch { /* Neutral card stays visible in the unfiltered catalogue. */ }
      const rows = (value) => Array.isArray(value) ? value.filter((item) => item && item.brand && item.brand_label && item.series && item.series_label) : [];
      return { node, id: node.dataset.model, title: node.querySelector('h2')?.textContent || '', shape: data.shape || '', matches: rows(data.matches), excludes: rows(data.excludes) };
    });
    if (!brandInput || !seriesInput || !count || !grid || !message || !messageTitle || !messageCopy || !messageLink || !filters || !resultsHead || !footnote) return;

    // Options and model assignments come from the same server-rendered fit data
    // used by product pages. There is no second product/series list in JavaScript.
    const brands = new Map();
    cards.forEach((card) => [...card.matches, ...card.excludes].forEach((row) => {
      if (!brands.has(row.brand)) brands.set(row.brand, { title: row.brand_label, series: new Map() });
      const brand = brands.get(row.brand);
      if (!brand.series.has(row.series)) brand.series.set(row.series, { id: row.series, title: row.series_label, models: new Set(), excluded: new Set() });
      const series = brand.series.get(row.series);
      if (card.matches.some((item) => item.brand === row.brand && item.series === row.series)) series.models.add(card.id);
      if (card.excludes.some((item) => item.brand === row.brand && item.series === row.series)) series.excluded.add(card.title);
    }));
    // Natural model order, independent of Shopify's lexical handle ordering.
    // New numeric models join automatically; other product names sort naturally.
    cards.sort((a, b) => {
      const aNumeric = /^\d+$/.test(a.id || '');
      const bNumeric = /^\d+$/.test(b.id || '');
      if (aNumeric && bNumeric) return Number(a.id) - Number(b.id);
      if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
      return a.title.localeCompare(b.title, 'de', { numeric: true, sensitivity: 'base' });
    });
    grid.prepend(...cards.map((card) => card.node));

    const isFinder = root.dataset.bsCatMode === 'finder';
    const state = { brand: 'all', series: 'all', shape: 'all', path: 'known' };
    const option = (value, title) => {
      const node = document.createElement('option');
      node.value = value;
      node.textContent = title;
      return node;
    };
    brandInput.replaceChildren(option('all', isFinder ? 'Bitte auswählen' : 'Alle Marken'));
    [...brands].sort((a, b) => a[1].title.localeCompare(b[1].title, 'de')).forEach(([id, brand]) => brandInput.append(option(id, brand.title)));
    brandInput.append(option('unknown', 'Andere Marke / Marke unbekannt'));

    function fillSeries() {
      const brand = brands.get(state.brand);
      seriesInput.replaceChildren(option('all', brand ? (isFinder ? 'Serie auswählen' : 'Alle Serien') : 'Zuerst Marke wählen'));
      if (brand) {
        brand.series.forEach((series) => seriesInput.append(option(series.id, series.title)));
        seriesInput.append(option('unknown', 'Andere Serie / weiß ich nicht'));
      }
      seriesInput.disabled = !brand;
      seriesInput.value = state.series;
      if (seriesField) seriesField.hidden = state.brand === 'unknown';
    }

    function showMessage(title, copy, offerHelp) {
      message.hidden = false;
      messageTitle.textContent = title;
      messageCopy.textContent = copy;
      messageLink.hidden = !offerHelp;
    }

    function updateSupportLinks(labels) {
      supportLinks.forEach(({ node, base }) => {
        let url;
        try { url = new URL(base, window.location.origin); } catch { return; }
        if (!['http:', 'https:'].includes(url.protocol)) return;
        url.searchParams.set('topic', 'Passform');
        ['brand', 'series', 'shape'].forEach((key) => {
          const parameter = `ceramic_${key}`;
          url.searchParams.delete(parameter);
          const value = String(labels[key] || '').trim().slice(0, 128);
          if (value) url.searchParams.set(parameter, value);
        });
        node.setAttribute('href', url.origin === window.location.origin ? url.pathname + url.search + url.hash : url.href);
      });
    }

    function update() {
      const shapePath = isFinder && state.path === 'shape';
      const unknownBrand = state.brand === 'unknown' || (isFinder && state.path === 'unknown');
      const unknownSeries = state.series === 'unknown';
      const preselection = shapePath || unknownBrand || unknownSeries;
      const selection = brands.get(state.brand)?.series.get(state.series);
      const awaitingSeries = isFinder && !preselection && !selection;
      const activeShape = preselection || !isFinder ? state.shape : 'all';
      updateSupportLinks({
        brand: shapePath ? '' : brands.get(state.brand)?.title,
        series: shapePath ? '' : selection?.title,
        shape: activeShape === 'all' ? '' : shapeNames[activeShape]
      });
      let found = 0;

      filters.hidden = shapePath;
      if (seriesField) seriesField.hidden = unknownBrand;
      if (shapePanel) shapePanel.hidden = !preselection;
      if (help) help.hidden = !(unknownBrand || unknownSeries);
      message.hidden = true;
      resultsHead.hidden = false;
      footnote.hidden = awaitingSeries;
      cards.forEach((card) => {
        const matchesBrand = state.brand === 'all' || card.matches.some((row) => row.brand === state.brand);
        const visible = !awaitingSeries && (preselection || (matchesBrand && (!selection || selection.models.has(card.id)))) && (activeShape === 'all' || card.shape === activeShape);
        card.node.hidden = !visible;
        if (visible) found += 1;
      });
      grid.hidden = found === 0;
      root.querySelectorAll('[data-bs-cat-path]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.bsCatPath === state.path)));
      root.querySelectorAll('[data-bs-cat-shape-button]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.bsCatShapeButton === state.shape)));
      root.querySelectorAll('[data-bs-cat-reset]').forEach((button) => { button.textContent = preselection ? 'Auswahl zurücksetzen' : 'Filter zurücksetzen'; });
      if (awaitingSeries) {
        count.textContent = 'Auswahl nach Keramikserie';
        showMessage('Welche Keramik hast du?', state.brand === 'all'
          ? 'Wähle Marke und genaue Serie. Falls du sie nicht kennst, starte mit der Form oder lass dir von uns helfen.'
          : `Wähle die genaue ${brands.get(state.brand).title}-Serie. Ähnlich benannte Serien können unterschiedliche Sitze benötigen.`, false);
        return;
      }
      count.textContent = `${found} ${found === 1 ? 'Modell' : 'Modelle'}${preselection ? ' zur Vorauswahl' : ''}${activeShape !== 'all' ? ` · ${shapeNames[activeShape]}` : ''}`;
      if (preselection) {
        showMessage('Eine Vorauswahl nach Form.', 'Diese Modelle sind noch keine bestätigte Passform. Gleiche die Maße und Befestigung mit deiner Keramik ab. Wenn du die Serie nicht kennst, helfen wir dir persönlich weiter.', true);
      }
      if (found === 0) {
        let copy = 'Für diese Auswahl ist kein Modell zugeordnet. Wir helfen dir beim Abgleich deiner Keramik.';
        if (selection?.excluded.size) {
          copy = `${[...selection.excluded].join(', ')} passt nicht zu ${brands.get(state.brand).title} ${selection.title}. Für diese Serie ist im Finder aktuell kein passender Sitz zugeordnet. Bitte kläre die Passform vor einer Bestellung mit uns.`;
        }
        showMessage('Kein eindeutiger Treffer.', copy, true);
      }
    }

    brandInput.addEventListener('change', () => {
      state.brand = brands.has(brandInput.value) ? brandInput.value : (brandInput.value === 'unknown' ? 'unknown' : 'all');
      state.series = 'all';
      state.shape = 'all';
      state.path = state.brand === 'unknown' ? 'unknown' : 'known';
      if (shapeInput) shapeInput.value = 'all';
      fillSeries();
      update();
    });
    seriesInput.addEventListener('change', () => {
      state.series = brands.get(state.brand)?.series.has(seriesInput.value) ? seriesInput.value : (seriesInput.value === 'unknown' ? 'unknown' : 'all');
      state.shape = 'all';
      if (shapeInput) shapeInput.value = 'all';
      update();
    });
    if (shapeInput) shapeInput.addEventListener('change', () => {
      state.shape = Object.hasOwn(shapeNames, shapeInput.value) ? shapeInput.value : 'all';
      update();
    });
    root.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !root.contains(button)) return;
      if (button.hasAttribute('data-bs-cat-reset')) {
        state.brand = 'all'; state.series = 'all'; state.shape = 'all'; state.path = 'known';
        brandInput.value = 'all';
        if (shapeInput) shapeInput.value = 'all';
        fillSeries(); update();
      } else if (button.hasAttribute('data-bs-cat-path')) {
        const path = button.dataset.bsCatPath;
        if (!['known', 'shape', 'unknown'].includes(path)) return;
        state.path = path;
        if (path === 'unknown') { state.brand = 'unknown'; state.series = 'all'; }
        if (path === 'known' && state.brand === 'unknown') { state.brand = 'all'; state.series = 'all'; }
        brandInput.value = state.brand;
        fillSeries(); update();
      } else if (button.hasAttribute('data-bs-cat-shape-button')) {
        const shape = button.dataset.bsCatShapeButton;
        if (Object.hasOwn(shapeNames, shape)) { state.shape = shape; if (shapeInput) shapeInput.value = shape; update(); }
      }
    });

    // Read only: preserve preview parameters and never rewrite the URL.
    const params = new URLSearchParams(window.location.search);
    const rawBrand = normalize(params.get('brand'));
    const initialBrand = [...brands].find(([id, brand]) => normalize(id) === rawBrand || normalize(brand.title) === rawBrand || (id === 'vb' && rawBrand === 'villeroyandboch'));
    if (initialBrand) state.brand = initialBrand[0];
    else if (rawBrand) { state.brand = 'unknown'; state.path = 'unknown'; }
    const rawSeries = normalize(params.get('series'));
    const initialSeries = [...(brands.get(state.brand)?.series.values() || [])].find((series) => normalize(series.id) === rawSeries || normalize(series.title) === rawSeries || (series.id === 'renova' && rawSeries === 'renovanr1plan'));
    if (initialSeries) state.series = initialSeries.id;
    else if (rawSeries && brands.has(state.brand)) state.series = 'unknown';
    const rawShape = normalize(params.get('shape'));
    const initialShape = Object.keys(shapeNames).find((id) => id === rawShape || normalize(shapeNames[id]) === rawShape);
    if (initialShape) { state.shape = initialShape; if (isFinder && state.brand === 'all') state.path = 'shape'; }
    const initialPath = params.get('path');
    if (isFinder && ['known', 'shape', 'unknown'].includes(initialPath)) {
      state.path = initialPath;
      if (initialPath === 'unknown') { state.brand = 'unknown'; state.series = 'all'; }
    }
    brandInput.value = state.brand;
    if (shapeInput) shapeInput.value = state.shape;
    fillSeries(); update();
    root.dataset.bsCatReady = 'true';
    root.classList.add('bs-cat--ready');
  }
  function scan(context) {
    if (context.matches?.('[data-bs-catalogue]')) initialize(context);
    context.querySelectorAll('[data-bs-catalogue]').forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
